import {
  Parameters,
  ParameterValueContext,
  ParameterValues,
  PARAMETER_KEY,
} from "calculation/parameter";
import { ParameterDefaultValueDto, ParameterDto, ScenarioDto } from "dtos/dtos";
import { useEffect } from "react";
import { ReactElement, useState } from "react";
import { ParameterInput } from "./ParameterInput";
import { ParameterSelect } from "./ParameterSelect";

function getParameterDefaultValue(
  parameter: ParameterDto,
  zoneTypKt: number | undefined,
  dichteKat: number | undefined
): ParameterDefaultValueDto | undefined {
  switch (parameter.dimensionsDefaultValue) {
    case 0:
      return parameter.defaultValues[0];
    case 1:
      return parameter.defaultValues.find((d) => d.zonTypKt === zoneTypKt);
    case 2:
      return parameter.defaultValues.find((d) => d.dichteKat === dichteKat);
    case 3:
      return parameter.defaultValues.find(
        (d) => d.zonTypKt === zoneTypKt && d.dichteKat === dichteKat
      );
  }
}

export function ParametersTable(props: {
  parameters: Parameters;
  parameterValues: ParameterValues;
  scenario: ScenarioDto;
  readonlyParameterSet: boolean;
  onChange: () => void;
}): ReactElement {
  const {
    parameters,
    parameterValues,
    scenario,
    readonlyParameterSet,
    onChange,
  } = props;
  const [selectedTab, setSelectedTab] = useState("");

  useEffect(() => {
    setSelectedTab(PARAMETER_KEY.paramAreaPerResident);
  }, []);

  const tabs: ReactElement[] = [];
  const metricValueTables: ReactElement[] = [];

  for (const p of parameters
    .getAllParameters()
    .filter((p) => p.manageLevel === 2)
    .sort((a, b) => -(a.manageLevel - b.manageLevel))) {
    tabs.push(
      <li
        key={p.key}
        className={`${
          selectedTab === p.key ? "bg-gray-100" : "hover:bg-gray-100/50"
        } p-4 cursor-pointer font-bold text-sm text-gray-500 rounded-t-md`}
        onClick={() => setSelectedTab(p.key)}
      >
        {p.name} {p.unit !== undefined && p.unit !== null ? `[${p.unit}]` : ""}
      </li>
    );

    if (p.dimensionsValue === 0) {
      const value = parameterValues.getValue(
        new ParameterValueContext(p.key, {
          name: "canton",
        })
      );

      if (value !== undefined) {
        const metricValueTable = (
          <div
            key={p.key}
            className={`${
              selectedTab === p.key ? "visible" : "hidden"
            } grid grid-cols-1`}
          >
            <div className="font-bold">Wert</div>
            {p.manageLevel === 2 && (
              <ParameterSelect
                options={parameters.getParameter(p.key)?.options}
                parameterValue={value}
                parameterDefaultValue={getParameterDefaultValue(
                  p,
                  undefined,
                  undefined
                )}
                disabled={readonlyParameterSet}
                onChange={onChange}
              />
            )}
            {p.manageLevel === 1 && (
              <div
                className={
                  value.value === value.defaultValue
                    ? "text-black"
                    : "text-green-200"
                }
              >
                {value.displayValue}
              </div>
            )}
          </div>
        );
        metricValueTables.push(metricValueTable);
      }
    } else if (p.dimensionsValue === 1) {
      const tableRows: ReactElement[] = [];

      const metricValueTable = (
        <div
          key={p.key}
          className={`${
            selectedTab === p.key ? "visible" : "hidden"
          } grid grid-cols-2 gap-x-4`}
        >
          <div className="font-bold">Ortsteil</div>
          <div className="font-bold">Wert</div>
          {tableRows}
        </div>
      );

      for (const district of scenario.districts) {
        const value = parameterValues.getValue(
          new ParameterValueContext(p.key, {
            name: "district",
            bfsNr1999: district.bfsNr1999,
          })
        );

        if (value !== undefined) {
          tableRows.push(
            <div key={district.bfsNr1999}>{district.gem1999}</div>
          );
          tableRows.push(
            p.manageLevel === 2 ? (
              <ParameterSelect
                options={p?.options.filter(
                  (o) =>
                    o.dichteKat === district.dichteKat || o.dichteKat === null
                )}
                parameterValue={value}
                parameterDefaultValue={getParameterDefaultValue(
                  p,
                  undefined,
                  district.dichteKat
                )}
                disabled={readonlyParameterSet}
                onChange={onChange}
              />
            ) : (
              <div
                key={`${district.bfsNr1999}-${value.displayValue}`}
                className={
                  value.value === value.defaultValue
                    ? "text-black"
                    : "text-green-200"
                }
              >
                {value.displayValue}
              </div>
            )
          );
        }
      }

      metricValueTables.push(metricValueTable);
    } else if (p.dimensionsValue === 2) {
      const tableRows: ReactElement[] = [];
      const metricValueTable = (
        <div
          key={p.key}
          className={`${
            selectedTab === p.key ? "visible" : "hidden"
          } grid grid-cols-11 w-full gap-y-2 items-center`}
        >
          <div className="text-gray-400 col-span-2">Ortsteil</div>
          <div className="text-gray-400 col-span-4">Zonentyp Gemeinde</div>
          <div className="text-gray-400 col-span-5">Wert</div>
          {tableRows}
        </div>
      );

      for (const district of scenario.districts) {
        for (const zoneGde of district.zonesGde
          .filter((z) => z.zonTypGde < 4000)
          .sort((a, b) => a.zonTypGde - b.zonTypGde)) {
          const value = parameterValues.getValue(
            new ParameterValueContext(p.key, {
              name: "districtZoneGde",
              bfsNr1999: district.bfsNr1999,
              zonTypGde: zoneGde.zonTypGde,
            })
          );
          if (value !== undefined) {
            tableRows.push(
              <div
                className="col-span-2"
                key={`district-${district.bfsNr1999}-${zoneGde.zonTypGde}`}
              >
                {district.gem1999}
              </div>
            );
            tableRows.push(
              <div
                key={`zone-${district.bfsNr1999}-${zoneGde.zonTypGde}`}
                className="col-span-4"
              >
                {zoneGde.zonTypGde} {zoneGde.nameZonTypGde}
              </div>
            );
            tableRows.push(
              p.manageLevel === 2 ? (
                p.key === PARAMETER_KEY.paramUez ? (
                  <div
                    key={`${district.bfsNr1999}-${zoneGde.zonTypGde}-${value.value}`}
                    className="col-span-5"
                  >
                    <ParameterInput
                      parameterValue={value}
                      parameterDefaultValue={getParameterDefaultValue(
                        p,
                        zoneGde.zonTypKt,
                        district.dichteKat
                      )}
                      disabled={readonlyParameterSet}
                      onChange={onChange}
                    />
                  </div>
                ) : (
                  <div
                    key={`${district.bfsNr1999}-${zoneGde.zonTypGde}-${value.value}`}
                    className="col-span-5"
                  >
                    <ParameterSelect
                      options={parameters
                        ?.getParameter(p.key)
                        ?.options.filter(
                          (o) =>
                            o.dichteKat === district.dichteKat ||
                            o.dichteKat === null
                        )}
                      parameterValue={value}
                      parameterDefaultValue={getParameterDefaultValue(
                        p,
                        zoneGde.zonTypKt,
                        district.dichteKat
                      )}
                      disabled={readonlyParameterSet}
                      onChange={onChange}
                    />
                  </div>
                )
              ) : (
                <div
                  key={`${district.bfsNr1999}-${zoneGde.zonTypGde}-${value.value}`}
                  className={`${
                    value.value === value.defaultValue
                      ? "text-black"
                      : "text-red-300"
                  } col-span-5`}
                >
                  {value.displayValue}
                </div>
              )
            );
          }
        }
      }

      metricValueTables.push(metricValueTable);
    }
  }

  return (
    <div>
      <ul className="mt-2 flex flex-row">{tabs}</ul>
      <div className="p-4 flex flex-col space-y-4 bg-gray-100 rounded-md">
        {metricValueTables}
      </div>
    </div>
  );
}
